// The owner's "This month" usage on My Subscription (docs/plan-limits-plan.md §7):
// one bar per metric against the business's effective limits — amber from 80 %,
// red from 100 %, "Unlimited" when the limit is null. Storage arrives in bytes.

import type { UsageMetricSnapshot } from '@bbpos/types';
import type { TenantUsage } from '@/types/tenant';
import { GLASS_PANEL, cn, toneClasses } from '@/components/features/subscriptions/design-tokens';
import {
  USAGE_METRIC_LABELS,
  formatBytes,
  usageTone,
} from '@/components/features/subscriptions/plan-entitlements';

const BAR_FILL: Record<ReturnType<typeof usageTone>, string> = {
  success: 'bg-gradient-to-r from-brand-500 to-indigo-500',
  warning: 'bg-gradient-to-r from-amber-400 to-orange-500',
  danger: 'bg-gradient-to-r from-rose-500 to-red-600',
};

function formatAmount(metric: UsageMetricSnapshot['metric'], n: number): string {
  return metric === 'storage_bytes' ? formatBytes(n) : n.toLocaleString('en-IN');
}

/** "October 2026" from the API's `2026-10`. */
function periodLabel(period: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function UsageBar({ snapshot }: { snapshot: UsageMetricSnapshot }) {
  const label = USAGE_METRIC_LABELS[snapshot.metric] ?? snapshot.metric;
  const unlimited = snapshot.limit === null;
  const tone = usageTone(snapshot.percent);
  // Usage can sit above a limit lowered later; clamp the bar, not the numbers.
  const width = unlimited ? 0 : Math.min(snapshot.percent ?? 0, 100);

  return (
    <div
      className="rounded-xl border border-neutral-100 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md animate-slide-up dark:border-neutral-700 dark:bg-neutral-800"
      data-testid={`usage-${snapshot.metric}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
            toneClasses(unlimited ? 'neutral' : tone).pill,
          )}
        >
          {unlimited ? 'Unlimited' : `${snapshot.percent}%`}
        </span>
      </div>
      <p className="mt-1 tabular-nums text-neutral-900 dark:text-white">
        <span className="text-lg font-bold">{formatAmount(snapshot.metric, snapshot.used)}</span>
        <span className="text-sm text-neutral-400 dark:text-neutral-500">
          {snapshot.limit === null
            ? ' used'
            : ` of ${formatAmount(snapshot.metric, snapshot.limit)}`}
        </span>
      </p>
      <div className="mt-2 h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-700">
        {!unlimited && (
          <div
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={width}
            className={cn('h-2 rounded-full transition-all duration-500', BAR_FILL[tone])}
            style={{ width: `${width}%` }}
          />
        )}
      </div>
      {/* Bills never block (plan §2) — say so, so a full bar does not stop the till. */}
      {snapshot.metric === 'invoices' && tone === 'danger' && (
        <p className="mt-2 text-xs font-medium text-rose-700 dark:text-rose-300">
          You can still make bills. Upgrade to stay within your plan.
        </p>
      )}
    </div>
  );
}

export function UsageSection({ usage }: { usage: TenantUsage }) {
  return (
    <div className={cn(GLASS_PANEL, 'p-6 mb-6 animate-slide-up')}>
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
            This month
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {periodLabel(usage.period)} · how much of your plan you have used
          </p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {usage.metrics.map((m) => (
          <UsageBar key={m.metric} snapshot={m} />
        ))}
      </div>
    </div>
  );
}
